import { ArrowUp, Link, Square } from 'lucide-react';
import { useCallback, useRef } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface ChatInputProps {
  /** 'large' for full-screen landing, 'compact' for split panel. */
  variant?: ('large' | 'compact') | undefined;
  /** Placeholder text for the textarea. Defaults to "Ask Lily anything...". */
  placeholder?: string | undefined;
  /**
   * Show the left-side attach / MCP-connect icon button. Defaults to true so the
   * global Lily Copilot keeps the affordance; the experiment-details chatbot
   * passes false because that surface doesn't accept attachments.
   */
  showAttachButton?: boolean | undefined;
  onSend: (content: string) => void;
  onAbort?: (() => void) | undefined;
  isStreaming?: boolean | undefined;
  disabled?: boolean | undefined;
}

/** Auto-resizing chat input — Enter sends, Shift+Enter newlines. Send button morphs to Stop while streaming. */
export function ChatInput({
  variant = 'compact',
  placeholder = 'Ask Lily anything...',
  showAttachButton = true,
  onSend,
  onAbort,
  isStreaming = false,
  disabled = false,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    const value = textareaRef.current?.value.trim();
    if (!value || disabled) return;

    onSend(value);
    if (textareaRef.current) {
      textareaRef.current.value = '';
      textareaRef.current.style.height = 'auto';
    }
  }, [onSend, disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (isStreaming) return;
        handleSubmit();
      }
    },
    [handleSubmit, isStreaming],
  );

  const handleInput = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${String(Math.min(textarea.scrollHeight, 200))}px`;
  }, []);

  const isLarge = variant === 'large';

  return (
    <div
      className={cn(
        'border-border bg-background flex flex-col rounded-2xl border shadow-sm',
        isLarge ? 'min-h-[120px]' : 'min-h-[56px]',
      )}
    >
      {/* Textarea area */}
      <div className={cn('flex-1 px-4', isLarge ? 'pt-4' : 'pt-3')}>
        <textarea
          ref={textareaRef}
          rows={isLarge ? 2 : 1}
          placeholder={placeholder}
          className={cn(
            'placeholder:text-muted-foreground w-full resize-none bg-transparent outline-none',
            isLarge ? 'text-base' : 'text-sm',
            disabled && 'cursor-not-allowed',
          )}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          disabled={disabled}
        />
      </div>

      {/* Bottom bar: optional MCP icon left, send button right (always). */}
      <div
        className={cn(
          'flex items-center px-3',
          showAttachButton ? 'justify-between' : 'justify-end',
          isLarge ? 'pb-3' : 'pb-2',
        )}
      >
        {showAttachButton ? (
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground h-9 w-9 cursor-pointer rounded-full"
            aria-label="Attach or connect MCP"
          >
            <Link className="h-5 w-5" />
          </Button>
        ) : null}

        {isStreaming ? (
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
            onClick={onAbort}
            aria-label="Stop generating"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            size="icon"
            className={cn(
              'bg-foreground text-background hover:bg-foreground/90 h-9 w-9 shrink-0 rounded-full',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            onClick={handleSubmit}
            disabled={disabled}
            aria-label="Send message"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
